import { TimeUnits } from '@shared/types'
import { convertToMs } from '@shared/utils'
import { useQuery } from '@tanstack/react-query'
import Pubnub, { type ListenerParameters } from 'pubnub'
import { createContext, memo, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useProfileQuery } from 'v2/pages/settings/queries'
import { useTabStore } from 'v2/state/tab'
import { type PubNubContextType, type PubNubRef, PubnubStatus } from 'v2/types/pubnub'
import { pubnubLogger } from 'v2/utils/logger'
import { executionService } from '../services/execution-service'
import { addPubNubEventListeners } from '../utils/pubnub.util'
import { getSettingsQueryKeys } from '../utils/query-key-management'
import { useAuth } from './use-auth'
import { usePubnubMutations } from './use-pubnub.mutations'

const PubNubContext = createContext<PubNubContextType | null>(null)

const pubnubConfig = {
  keepAlive: true,
  heartbeatInterval: 60,
  retryConfiguration: {
    maximumRetry: 6,
    delay: 10000,
  },
  restore: true,
}
export const PubNubProvider = memo(
  ({ children }: { children: React.ReactNode }) => {
    const { data: profile } = useProfileQuery()
    const { userAuthenticated } = useAuth()
    const { publish_key: publishKey, subscribe_key: subscribeKey } =
      profile?.organizations[0].pubnub_keyset_data || {}
    const pubnubRef = useRef<PubNubRef | null>(null)
    const listenerRef = useRef<ListenerParameters | null>(null)
    const setPubnubStatus = useTabStore((state) => state.setPubnubStatus)
    const [tokenExpiry, setTokenExpiry] = useState<number>(0) // minutes
    const pubnubActions = usePubnubMutations({ pubnubRef })

    // Register PubNub
    useEffect(() => {
      // only initialize if the pubnub object is not already initialized
      if (userAuthenticated && publishKey && subscribeKey && !pubnubRef.current?.isRegistered) {
        pubnubLogger.log('PubNub initializing')
        pubnubRef.current = {
          pubnub: new Pubnub({
            publishKey,
            subscribeKey,
            userId: profile?.organizations[0].id || '',
            ...pubnubConfig,
          }),
          isRegistered: true,
        }
        window.cx.pubnubInstance = pubnubRef.current.pubnub
      }
      // Unregister PubNub if user is not authenticated (logged out) and if it is already registered
      if (!userAuthenticated && pubnubRef.current?.isRegistered) {
        pubnubLogger.log('PubNub unregistering')
        pubnubRef.current.pubnub.setToken('')
        try {
          pubnubRef.current.pubnub.unsubscribeAll()
          if (listenerRef.current) {
            pubnubLogger.debug('Removing pubnub listeners')
            pubnubRef.current.pubnub.removeListener(listenerRef.current)
            listenerRef.current = null
          }
          pubnubRef.current.pubnub.stop?.()
        } catch (error) {
          pubnubLogger.error('Error unregistering PubNub', error)
        }
        setPubnubStatus(PubnubStatus.DISCONNECTED)
        // Clear global instance to avoid stale refs elsewhere
        window.cx.pubnubInstance = undefined
        pubnubRef.current.isRegistered = false
      }
    }, [profile, userAuthenticated, publishKey, subscribeKey])

    // Add/Remove PubNub listeners
    useEffect(() => {
      if (pubnubRef.current?.pubnub && userAuthenticated) {
        addPubNubEventListeners(listenerRef, pubnubRef)
      }
      const removeEventListeners = () => {
        if (listenerRef.current) {
          pubnubLogger.debug('Removing pubnub listeners')
          pubnubRef.current?.pubnub?.removeListener(listenerRef.current)
          listenerRef.current = null
        }
      }
      return removeEventListeners
    }, [pubnubRef.current?.isRegistered, userAuthenticated])

    const { isError, isFetching } = useQuery({
      queryKey: getSettingsQueryKeys().pubnubToken(profile?.organizations[0].id || ''),
      enabled: !!profile?.organizations[0].id && userAuthenticated,
      staleTime: convertToMs(tokenExpiry, TimeUnits.Minutes), // it becomes stale at ttl
      refetchInterval: convertToMs((tokenExpiry || 10) * 0.8, TimeUnits.Minutes), // refetch 20% before expiry
      refetchOnMount: 'always',
      refetchOnWindowFocus: true,
      meta: { silent: true },
      queryFn: async () => {
        try {
          // Store timetoken
          pubnubLogger.log('Generating token')
          const response = await executionService.generateToken(profile?.organizations[0].id || '')
          const parsedToken = pubnubRef.current?.pubnub.parseToken(response.data)
          pubnubLogger.log('Token generated - token expiry in ', parsedToken?.ttl, 'minutes')
          setTokenExpiry(parsedToken?.ttl || 0)
          pubnubLogger.log('PubNub setting token')
          pubnubRef.current?.pubnub.setToken(response.data)
          const channels = [`aom.${profile?.organizations[0].id}`]
          const orgId = profile?.organizations[0].org_id
          if (orgId) channels.push(`org.${orgId}`)
          pubnubActions.subscribeMutation.mutate({ channels })

          setPubnubStatus(PubnubStatus.CONNECTED)
          pubnubLogger.local('PubNub connected')
          return response.data ?? ''
        } catch (error) {
          pubnubLogger.error('Error generating token', error)
          throw error
        }
      },
    })

    useEffect(() => {
      if (isError && !isFetching) setPubnubStatus(PubnubStatus.DISCONNECTED)
      else if (isFetching) setPubnubStatus(PubnubStatus.CONNECTING)
    }, [isError, setPubnubStatus, isFetching])

    const value = useMemo(() => ({ ...pubnubActions, pubnubRef }), [pubnubActions])

    return <PubNubContext.Provider value={value}>{children}</PubNubContext.Provider>
  },
  () => true,
)

export function usePubNub() {
  const pubNub = useContext(PubNubContext)
  if (!pubNub) {
    throw new Error('usePubNub must be used within a PubNubProvider')
  }
  return pubNub
}
